import api, { API_ENDPOINTS } from '@/utils/api';
import { AuthUser } from '@/types/users';
import { normalizeUser } from './users';

// ----------------------------------------------------------------------
//
// Auth service. Phase 7 rewrite — all of this used to be a mix of:
//   - Next.js API routes (`internalApi`) that proxied + decrypted Xano
//     responses (cryptr); those are gone.
//   - Goboarding-specific link generators; the new backend (`KycModule`)
//     creates the KYC session and returns the URL directly.
//
// Now everything just hits the NestJS backend.
//
// ----------------------------------------------------------------------

export type LoginResponse = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
};

export const postLogin = async (email: string, password: string): Promise<LoginResponse> => {
  const raw = await api.post(API_ENDPOINTS.auth.login, {
    params: { email, password },
  });
  return { ...raw, user: normalizeUser(raw.user) };
};

/**
 * Ask the backend to create a KYC session for the given user and return
 * the URL the customer should follow (im-kyc.numenai.tech/...).
 * Replaces the legacy `getGoboardingAuthLink` + `getFaceRegistrationLink`
 * round-trips against Vercel.
 *
 * `flowId` is required by the backend (validated against the market's
 * configured signupFlowId / faceRegistrationFlowId). All Intelli Market
 * markets share `signupFlowId = 4` today, so we default to that.
 */
export const createKycSession = (
  email: string,
  flowId: number = 4,
  userId?: string
): Promise<{ transactionId: string; kycUrl: string }> =>
  api.post('/kyc/sessions', {
    params: { email, flowId, ...(userId ? { userId } : {}) },
  });

/**
 * Look up an onboarder by its single-use transactionId. Backend gates
 * this to authenticated requests — the cryptr-encrypted Xano endpoint
 * is gone.
 */
export const getIdentityInfo = async (transactionId: string) =>
  api.get(`/onboarders/${transactionId}`);

/**
 * Current user. Backend returns plain JSON now (no more cryptr
 * decoding via a Next.js API route). camelCase payload is shimmed
 * back to snake_case so legacy components keep compiling.
 */
export const getAuthUser = async (): Promise<AuthUser> => {
  const raw = await api.get(API_ENDPOINTS.auth.me);
  return normalizeUser(raw);
};

/**
 * Public status of a KYC session. Polled by the register screen while the
 * user is doing the facial check in the other tab — that tab can't talk
 * back to us (it's opened with window.open and closes itself), so asking
 * the backend is the only reliable signal that the check finished.
 */
export type KycSessionStatus = {
  transactionId: string;
  email: string;
  status: 'INITIATED' | 'OTP_VERIFIED' | 'DOCUMENT_UPLOADED' | 'SELFIE_UPLOADED' | 'VERIFIED' | 'FAILED' | 'SUBMITTED';
  faceVerified: boolean | null;
  faceSimilarity: number | null;
  submittedAt: string | null;
};

export const getKycSessionStatus = (transactionId: string): Promise<KycSessionStatus> =>
  api.get(`/kyc/sessions/${transactionId}/status`);

/**
 * Finish signup. Unlike `/auth/register` (which only inserts a bare user
 * row), this runs the backend's enrolment saga: QR code, Hikvision person
 * + access privilege, POS client. Without it the account exists but the
 * door never opens.
 *
 * The email is NOT sent — the backend takes it from the KYC session, which
 * already proved ownership via the OTP.
 */
export const registerWithKyc = (payload: {
  transactionId: string;
  password: string;
  firstName: string;
  lastName: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  marketId: string;
}) => api.post('/auth/register-kyc', { params: payload });
